/**
 * ProService - API service for Klorah Pro features
 * Handles leads, automation, booking, payments, and AI features
 */

import { auth } from '../firebase';

const BASE_URL = process.env.REACT_APP_API_URL || 'https://klorah-fast-server-my8l.onrender.com/api';

class ProService {
  /**
   * Get authentication headers with Firebase ID token
   */
  async getAuthHeaders() {
    const user = auth.currentUser;
    if (!user) throw new Error('User not authenticated');

    const idToken = await user.getIdToken();
    return {
      'Authorization': `Bearer ${idToken}`,
      'Content-Type': 'application/json',
    };
  }

  /**
   * Make authenticated API request
   */
  async request(endpoint, options = {}) {
    const headers = await this.getAuthHeaders();
    const response = await fetch(`${BASE_URL}${endpoint}`, {
      ...options,
      headers: {
        ...headers,
        ...options.headers,
      },
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ detail: response.statusText }));
      throw new Error(error.detail || 'Request failed');
    }

    return response.json();
  }

  // ═══════════════════════════════════════════════════════════
  // LEADS & PIPELINE
  // ═══════════════════════════════════════════════════════════

  async createLead(userId, leadData) {
    return this.request('/leads/create', {
      method: 'POST',
      body: JSON.stringify({ userId, lead: leadData }),
    });
  }

  async updateLead(userId, leadId, updates) {
    return this.request('/leads/update', {
      method: 'PUT',
      body: JSON.stringify({ userId, leadId, updates }),
    });
  }

  async getPipeline(userId) {
    return this.request(`/leads/pipeline/${userId}`);
  }

  async getHotLeads(userId, threshold = 70) {
    return this.request(`/leads/hot/${userId}?threshold=${threshold}`);
  }

  async addLeadActivity(userId, leadId, type, content, metadata = null) {
    return this.request('/leads/activity', {
      method: 'POST',
      body: JSON.stringify({ userId, leadId, type, content, metadata }),
    });
  }

  async recordEngagement(userId, leadId, engagementType, metadata = null) {
    return this.request(`/leads/record-engagement?user_id=${userId}&lead_id=${leadId}&engagement_type=${engagementType}`, {
      method: 'POST',
      body: JSON.stringify(metadata || {}),
    });
  }

  async getLeadAnalytics(userId, period = '30d') {
    return this.request(`/leads/analytics/${userId}?period=${period}`);
  }

  // ═══════════════════════════════════════════════════════════
  // AUTOMATION & FUNNELS
  // ═══════════════════════════════════════════════════════════

  async createFunnel(userId, funnel) {
    return this.request('/automation/funnels/create', {
      method: 'POST',
      body: JSON.stringify({ userId, funnel }),
    });
  }

  async getFunnels(userId) {
    return this.request(`/automation/funnels/${userId}`);
  }

  async updateFunnel(userId, funnelId, funnel) {
    return this.request(`/automation/funnels/${userId}/${funnelId}`, {
      method: 'PUT',
      body: JSON.stringify(funnel),
    });
  }

  async deleteFunnel(userId, funnelId) {
    return this.request(`/automation/funnels/${userId}/${funnelId}`, {
      method: 'DELETE',
    });
  }

  async triggerAutomation(userId, triggerType, leadId, metadata = null) {
    return this.request(`/automation/trigger/${userId}?trigger_type=${triggerType}&lead_id=${leadId}`, {
      method: 'POST',
      body: JSON.stringify(metadata || {}),
    });
  }

  async getAutomationQueue(userId) {
    return this.request(`/automation/queue/${userId}`);
  }

  async getAutomationHistory(userId, leadId = null, limit = 50) {
    const params = new URLSearchParams({ limit });
    if (leadId) params.append('lead_id', leadId);
    return this.request(`/automation/history/${userId}?${params}`);
  }

  // ═══════════════════════════════════════════════════════════
  // BOOKING
  // ═══════════════════════════════════════════════════════════

  async getBookingPage(slug) {
    return this.request(`/booking/page/${slug}`);
  }

  async getAvailability(userId, serviceId, date) {
    return this.request(`/booking/availability/${userId}?service_id=${serviceId}&date=${date}`);
  }

  async createBooking(bookingData) {
    return this.request('/booking/create', {
      method: 'POST',
      body: JSON.stringify(bookingData),
    });
  }

  async getBookings(userId, status = null, startDate = null, endDate = null) {
    const params = new URLSearchParams();
    if (status) params.append('status', status);
    if (startDate) params.append('start_date', startDate);
    if (endDate) params.append('end_date', endDate);
    return this.request(`/booking/bookings/${userId}?${params}`);
  }

  async updateBookingStatus(userId, bookingId, status) {
    return this.request(`/booking/bookings/${userId}/${bookingId}/status`, {
      method: 'PUT',
      body: JSON.stringify({ status }),
    });
  }

  async saveBookingSettings(userId, settings) {
    return this.request(`/booking/settings/${userId}`, {
      method: 'POST',
      body: JSON.stringify(settings),
    });
  }

  async getBookingSettings(userId) {
    return this.request(`/booking/settings/${userId}`);
  }

  async getUpcomingBookings(userId, days = 7) {
    return this.request(`/booking/upcoming/${userId}?days=${days}`);
  }

  async getCalendarBookings(userId, month) {
    return this.request(`/booking/calendar/${userId}?month=${month}`);
  }

  // ═══════════════════════════════════════════════════════════
  // PAYMENT SCHEDULES
  // ═══════════════════════════════════════════════════════════

  async createPaymentSchedule(invoiceId, userId, schedule) {
    return this.request('/payment-schedules/create', {
      method: 'POST',
      body: JSON.stringify({ invoiceId, userId, schedule }),
    });
  }

  async markInstallmentPaid(invoiceId, installmentId, userId, paidAt = null) {
    return this.request('/payment-schedules/mark-paid', {
      method: 'POST',
      body: JSON.stringify({ invoiceId, installmentId, userId, paidAt }),
    });
  }

  async getPendingReminders(userId) {
    return this.request(`/payment-schedules/pending-reminders/${userId}`);
  }

  async sendPaymentReminder(invoiceId, installmentId, userId) {
    return this.request(`/payment-schedules/send-reminder?invoice_id=${invoiceId}&installment_id=${installmentId}&user_id=${userId}`, {
      method: 'POST',
    });
  }

  async getOverduePayments(userId) {
    return this.request(`/payment-schedules/overdue/${userId}`);
  }

  // ═══════════════════════════════════════════════════════════
  // AI FEATURES
  // ═══════════════════════════════════════════════════════════

  async draftEmail(userId, context, tone, leadId = null, clientId = null, additionalContext = null) {
    return this.request('/ai/draft-email', {
      method: 'POST',
      body: JSON.stringify({
        userId,
        leadId,
        clientId,
        context,
        tone,
        additionalContext,
      }),
    });
  }

  async getSmartSuggestions(userId, leadId) {
    return this.request('/ai/smart-suggestions', {
      method: 'POST',
      body: JSON.stringify({ userId, leadId }),
    });
  }

  async generateQuote(userId, clientName, projectType, projectDate = null, details = null) {
    return this.request('/ai/generate-quote', {
      method: 'POST',
      body: JSON.stringify({ userId, clientName, projectType, projectDate, details }),
    });
  }

  async configureAlerts(userId, config) {
    return this.request('/ai/configure-alerts', {
      method: 'POST',
      body: JSON.stringify({ userId, ...config }),
    });
  }

  async getAlerts(userId, unreadOnly = true, limit = 20) {
    return this.request(`/ai/alerts/${userId}?unread_only=${unreadOnly}&limit=${limit}`);
  }

  async markAlertRead(userId, alertId) {
    return this.request(`/ai/alerts/${userId}/${alertId}/read`, {
      method: 'PUT',
    });
  }

  async getBusinessInsights(userId) {
    return this.request(`/ai/insights/${userId}`);
  }

  async analyzeLead(userId, leadId) {
    return this.request(`/ai/analyze-lead?user_id=${userId}&lead_id=${leadId}`, {
      method: 'POST',
    });
  }
}

const proServiceInstance = new ProService();
export default proServiceInstance;
